'use client';

import { FC, useState } from 'react';
import { useTranslations } from '@/hooks/useTranslations';
import { useSearchParams } from 'next/navigation';
import { TwoFAForm } from './2fa-form';
import { PasswordSettingsForm } from './password-change-form';
import { ExtendedUser } from '@/types/next-auth';
import { AccountSettingsForm } from './account-settings-form';
import { YearlyLimitsForm } from './yearly-limits-form';
import { FeatureSettingsForm } from './feature-settings-form';
import { InviteAccountantForm } from './invite-accountant-form';
import { DeleteAccountSection } from './delete-account-section';
import { BillingSection } from './billing-section';
import { TLimit } from '@/types';
import type { LiveSubscription } from '@/lib/stripe/get-live-subscription';

interface ISettings {
  user: ExtendedUser;
  limits: TLimit[];
  liveSubscription: LiveSubscription | null;
}

export const AccountSettings: FC<ISettings> = (props) => {
  const t = useTranslations('UserSettings');

  const searchParams = useSearchParams();
  const section = searchParams.get('section') || 'details';

  const [activeSection, setActiveSection] = useState(section);

  const handleNavigation = (section: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('section', section);
    window.history.pushState({}, '', url);
    setActiveSection(section);
  };

  const renderForm = () => {
    switch (activeSection) {
      case 'details':
        return (
          <AccountSettingsForm
            user={props.user}
            redirectOnSuccess="/dashboard"
          />
        );

      case 'password':
        return <PasswordSettingsForm hasPassword={!!props.user.hasPassword} />;

      case '2fa':
        return props.user.hasPassword ? <TwoFAForm user={props.user} /> : null;

      case 'limits':
        return <YearlyLimitsForm limits={props.limits} />;

      case 'features':
        return <FeatureSettingsForm user={props.user} />;

      case 'accountant':
        return <InviteAccountantForm />;

      case 'billing':
        return (
          <BillingSection
            user={props.user}
            liveSubscription={props.liveSubscription}
          />
        );

      case 'danger':
        return (
          <DeleteAccountSection
            userId={props.user.id!}
            userEmail={props.user.email!}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col py-10 px-5 md:flex-row md:gap-5 md:p-10">
      <div className="flex flex-col w-full gap-2 mr-5 mb-5 md:mb-0 md:w-60">
        <div
          onClick={() => handleNavigation('details')}
          className={`cursor-pointer p-2 hover:bg-secondary ${
            activeSection === 'details' ? 'text-primary bg-secondary' : ''
          }`}
        >
          {t('myDetails')}
        </div>

        <div
          onClick={() => handleNavigation('password')}
          className={`cursor-pointer p-2 hover:bg-secondary ${
            activeSection === 'password' ? 'text-primary bg-secondary' : ''
          }`}
        >
          {t('password')}
        </div>

        {props.user.hasPassword && (
          <div
            onClick={() => handleNavigation('2fa')}
            className={`cursor-pointer p-2 hover:bg-secondary ${
              activeSection === '2fa' ? 'text-primary bg-secondary' : ''
            }`}
          >
            {t('2FA')}
          </div>
        )}

        <div
          onClick={() => handleNavigation('limits')}
          className={`cursor-pointer p-2 hover:bg-secondary ${
            activeSection === 'limits' ? 'text-primary bg-secondary' : ''
          }`}
        >
          {t('yearlyLimits')}
        </div>

        <div
          onClick={() => handleNavigation('features')}
          className={`cursor-pointer p-2 hover:bg-secondary ${
            activeSection === 'features' ? 'text-primary bg-secondary' : ''
          }`}
        >
          {t('features')}
        </div>

        {props.user.role !== 'ACCOUNTANT' && (
          <div
            onClick={() => handleNavigation('accountant')}
            className={`cursor-pointer p-2 hover:bg-secondary ${
              activeSection === 'accountant' ? 'text-primary bg-secondary' : ''
            }`}
          >
            {t('inviteAccountant')}
          </div>
        )}

        {props.user.role !== 'ACCOUNTANT' && (
          <div
            onClick={() => handleNavigation('billing')}
            className={`cursor-pointer p-2 hover:bg-secondary ${
              activeSection === 'billing' ? 'text-primary bg-secondary' : ''
            }`}
          >
            Billing
          </div>
        )}

        <div
          onClick={() => handleNavigation('danger')}
          className={`cursor-pointer p-2 hover:bg-secondary text-destructive ${
            activeSection === 'danger' ? 'bg-secondary' : ''
          }`}
        >
          {t('dangerZone')}
        </div>
      </div>

      {renderForm()}
    </div>
  );
};
